import * as React from 'react';

import get from 'lodash/get';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {CheckboxButton} from 'shells/button/checkbox-button/checkbox-button';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import type {Option, Options} from 'shells/select-picker-generic/types';

import styles from './funnel-form.module.css';

type Stageset = {
    type: 'stagesets';
    name: string;
    id: string;
};

const funnelFilters = [
    {
        name: 'singleAssignee',
        key: 'filter.owner',
    },
    {
        name: 'tags',
        key: 'filter.tags',
    },
    {
        name: 'sources',
        key: 'filter.sources',
    },
    {
        name: 'products',
        key: 'filter.products',
    },
    {
        name: 'territory',
        key: 'filter.territories',
    },
    {
        name: 'priority',
        key: 'filter.priority',
    },
];

type Props = {
    dash: any;
    value: string;
    schema: any;
    onFilterSubmit: (data: FilterObject, key: string) => void;
    onFilterRemove: (filterKey: string) => void;
    onFormFieldChange: (key: string, newValue: string) => void;
};

export class FunnelForm extends React.Component<Props> {
    hasMultipleFunnels: boolean;
    defaultStageset: Stageset | null | undefined;

    constructor(props: Props) {
        super(props);

        this.hasMultipleFunnels = NutCapabilities && NutCapabilities.multipleFunnel.isAvailable;

        // Default to the first stage set
        if (this.props.dash.id === 'new-funnel') {
            this.defaultStageset = props.schema.stageset.enum[0];
        }
    }

    render() {
        const {schema} = this.props;
        const formValues = Routing.deparam(this.props.value);
        const stagesets = schema.stageset ? schema.stageset.enum : [];
        const options: Options = stagesets.map((stageset: Stageset) => ({
            value: stageset.id,
            label: stageset.name,
        }));
        const selectedId = formValues.stageset || (this.defaultStageset && this.defaultStageset.id);

        return (
            <div className={styles.container}>
                {this.hasMultipleFunnels && stagesets.length > 1 ? (
                    <div className={styles['funnel-selection']}>
                        <div className={styles.label}>Funnel</div>
                        <SelectPickerGeneric
                            name='stageset_id'
                            value={options.filter(({value}) => value === selectedId)}
                            options={options}
                            isSearchable={false}
                            onChange={(data: Option) => {
                                this.props.onFormFieldChange('stageset', data.value);
                            }}
                        />
                    </div>
                ) : undefined}
                <div className={styles['selectable-filters']}>
                    <div className={styles.label}>Filters</div>
                    {funnelFilters.map((filter) => {
                        const fieldName = filter.name;
                        const filterSchema = this.props.schema[fieldName];

                        if (filterSchema.type === 'boolean' && fieldName === 'priority') {
                            return (
                                <CheckboxButton
                                    key={filter.key}
                                    label={filterSchema.title}
                                    checked={Boolean(get(formValues, filter.key))}
                                    size='small'
                                    onChange={(isChecked: boolean) => {
                                        if (!isChecked) {
                                            this.props.onFilterRemove(filter.key);
                                        } else {
                                            const dataToSubmit = {
                                                [fieldName]: {
                                                    anyAll: 'any',
                                                    data: [
                                                        {
                                                            data: 'Hot',
                                                        },
                                                    ],
                                                },
                                            } as const;

                                            this.props.onFilterSubmit(dataToSubmit, filter.key);
                                        }
                                    }}
                                />
                            );
                        }

                        return (
                            <FilterCapsule
                                key={filter.key}
                                filter={get(formValues, filter.key)}
                                filterName={filter.name}
                                schemaField={filterSchema}
                                onSubmit={(data: FilterObject) =>
                                    this.props.onFilterSubmit(data, filter.key)
                                }
                                onRemove={() => this.handleFilterRemove(filter.key)}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    handleFilterRemove = (key: string) => {
        this.props.onFilterRemove(key);
    };
}
